/**
 * Self-serve account confirmation page (Phase 0 bug fix).
 * Fan opens email link → lands here with ?username= + ?code= →
 * we POST /api/fan/confirm-account, then offer next step (sign in).
 *
 * Backend wiring lives in admin-api fan-profile; this page is the
 * landing target the email template points at.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fanCognitoConfigured } from '../auth/fan-auth';
import { useDocumentTitle } from '../lib/useDocumentTitle';

type State = 'idle' | 'working' | 'done' | 'error' | 'bad-link';

export function Confirm() {
  useDocumentTitle('帳號驗證');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<State>('idle');
  const [msg, setMsg] = useState<string>('');

  const username = params.get('username') ?? params.get('email') ?? '';
  const code = params.get('code') ?? '';

  const attempt = useCallback(async () => {
    if (!username || !code) {
      setState('bad-link');
      setMsg('驗證連結缺少必要參數，可能是被截斷或複製錯誤。');
      return;
    }
    setState('working');
    setMsg('');
    try {
      const base = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_API_BASE_URL?.replace(/\/+$/, '');
      if (!base) throw new Error('前端未設定 VITE_API_BASE_URL（請聯絡管理員）');
      const res = await fetch(`${base}/api/fan/confirm-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, code }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status} ${text.slice(0, 200)}`);
      }
      setState('done');
    } catch (e) {
      setState('error');
      setMsg((e as Error).message);
    }
  }, [username, code]);

  useEffect(() => { void attempt(); }, [attempt]);

  return (
    <div className="max-w-md mx-auto px-3 sm:px-4 py-6 sm:py-12">
      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6 space-y-5" aria-live="polite" aria-busy={state === 'working'}>
        {/* Visual state icon — big circle so the outcome is obvious
            before the user reads any copy. Drives the colour of the
            sub-text below. */}
        <div className="flex flex-col items-center text-center">
          {state === 'working' && (
            <div className="h-16 w-16 rounded-full bg-slate-800 flex items-center justify-center mb-3">
              <span className="inline-block h-8 w-8 rounded-full border-[3px] border-slate-600 border-t-indigo-400 animate-spin" aria-hidden="true" />
            </div>
          )}
          {state === 'done' && (
            <div className="relative mb-3">
              {/* Two expanding rings that fade out — one-shot celebratory
                  pulse anchored to the success checkmark. CSS keyframes
                  only run once via animation-iteration-count: 1, baked
                  into the class. */}
              <span aria-hidden="true" className="nx-confirm-ring nx-confirm-ring-1" />
              <span aria-hidden="true" className="nx-confirm-ring nx-confirm-ring-2" />
              <div className="relative h-16 w-16 rounded-full bg-blue-500/15 border border-blue-500/40 flex items-center justify-center text-blue-300 text-3xl shadow-[0_0_24px_rgba(59,130,246,0.5)]">
                ✓
              </div>
            </div>
          )}
          {state === 'error' && (
            <div className="h-16 w-16 rounded-full bg-rose-500/15 border border-rose-500/40 flex items-center justify-center mb-3 text-rose-300 text-3xl">
              ✕
            </div>
          )}
          {state === 'bad-link' && (
            <div className="h-16 w-16 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mb-3 text-amber-300 text-3xl">
              !
            </div>
          )}
          <h1 className="text-xl sm:text-2xl font-semibold">
            {state === 'working' && '驗證中…'}
            {state === 'done' && '驗證完成！'}
            {state === 'error' && '驗證失敗'}
            {state === 'bad-link' && '連結不完整'}
            {state === 'idle' && '帳號驗證'}
          </h1>
        </div>

        {state === 'done' && (
          <div className="space-y-4">
            <p className="text-slate-300 text-sm text-center">
              你的 email 已通過驗證。{fanCognitoConfigured() ? '按下方按鈕登入並開始使用粉絲平台。' : '請聯絡管理員以啟用登入功能。'}
            </p>
            {/* Stack on mobile, side-by-side on sm+. Primary CTA full-
                width + bigger touch target than the old pill. */}
            <div className="flex flex-col sm:flex-row gap-2">
              {fanCognitoConfigured() && (
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="flex-1 rounded-lg bg-violet-500 hover:bg-violet-400 active:scale-[0.98] text-white font-medium py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-300 shadow-[0_8px_20px_-8px_rgba(124,70,243,0.6)]"
                >前往登入</button>
              )}
              <button
                type="button"
                onClick={() => navigate('/', { replace: true })}
                className="flex-1 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-500"
              >前往首頁</button>
            </div>
          </div>
        )}

        {state === 'error' && (
          <div className="space-y-4" role="alert">
            <p className="text-slate-400 text-sm break-words text-center">{msg}</p>
            <p className="text-slate-500 text-xs text-center">
              如果你已經透過其他裝置完成驗證，可以直接前往登入。
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => { void attempt(); }}
                className="flex-1 rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-medium py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-300"
              >重試</button>
              {fanCognitoConfigured() && (
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="flex-1 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-500"
                >前往登入</button>
              )}
              <button
                type="button"
                onClick={() => navigate('/', { replace: true })}
                className="flex-1 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-500"
              >前往首頁</button>
            </div>
          </div>
        )}

        {state === 'bad-link' && (
          <div className="space-y-3" role="alert">
            <p className="text-slate-400 text-sm text-center">{msg}</p>
            <p className="text-slate-500 text-xs text-center">
              請從註冊郵件的「驗證連結」按鈕重新點擊；如果還是失敗，請重新觸發註冊流程。
            </p>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="w-full rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-medium py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-300"
            >前往首頁</button>
          </div>
        )}
      </div>
    </div>
  );
}
